import React, { useState } from "react";
import "../styles/Global.css";
import styles from "./Login.module.css";
import WelcomePanel from "../components/auth/WelcomePanel";
import ForgotPasswordForm from "../components/auth/ForgotPasswordForm";
import { useAuthContext } from "../contexts/AuthContext";
import Footer from "../components/Footer";

/**
 * Forgot password page component
 * Uses glassmorphism styling for a modern, translucent appearance
 *
 * @returns {JSX.Element} - Rendered forgot password page
 */
const ForgotPassword: React.FC = () => {
  const { sendPasswordResetEmail, loading } = useAuthContext();
  const [resetStatus, setResetStatus] = useState<{
    success: boolean;
    message: string | null;
  }>({
    success: false,
    message: null,
  });

  /**
   * Handle forgot password form submission
   * @param {string} email - User email
   */
  const handleForgotPassword = async (email: string) => {
    setResetStatus({ success: false, message: null });

    const { error } = await sendPasswordResetEmail(email);

    if (error) {
      setResetStatus({
        success: false,
        message:
          error.message || "Failed to send reset email. Please try again.",
      });
      // Show error notification
      (window as any).showNotification?.(
        "Failed to send reset email. Please try again.",
        "error"
      );
    } else {
      setResetStatus({
        success: true,
        message: "Password reset email sent! Please check your inbox.",
      });
      // Show success notification
      (window as any).showNotification?.(
        `📧 Password reset link sent! Check your inbox at ${email} and follow the instructions.`,
        "success"
      );
    }
  };

  return (
    <>
      <div className={`${styles.authLayoutOuter} ${styles.forgotPasswordPage}`}>
        <div className={styles.authLayoutContent}>
          {/* Left panel with welcome message */}
          <div className={styles.leftPanel}>
            <WelcomePanel
              title="Password Recovery"
              subtitle="We'll help you reset your password and get back to your account"
            />
          </div>

          {/* Right panel with forgot password form */}
          <div className={styles.rightPanel}>
            <ForgotPasswordForm
              onSubmit={handleForgotPassword}
              isLoading={loading}
              success={resetStatus.success}
              message={resetStatus.message}
            />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ForgotPassword;
